// FILENAME: /frontend/src/theme.js
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

export const createAppTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';
  const cardBorder = alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.08 : 0.08);
  const topHighlight = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.70)';
  const paperTopHighlight = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.75)';
  const bottomShadow = isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.14)';
  const cardShadow = `inset 0 1px 0 ${topHighlight}, 0 14px 28px ${bottomShadow}`;
  const elevatedSurfaceGradient = isDark
    ? 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,1))';
  const scrollbarTrack = alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.06 : 0.08);
  const scrollbarThumb = alpha(isDark ? '#e2e8f0' : '#334155', isDark ? 0.32 : 0.35);
  const scrollbarThumbHover = alpha(isDark ? '#f8fafc' : '#1e293b', isDark ? 0.5 : 0.55);

  const base = createTheme({
    palette: {
      mode,
      primary: { main: isDark ? '#60a5fa' : '#2563eb' }, // blue
      secondary: { main: isDark ? '#a78bfa' : '#7c3aed' }, // violet
      background: {
        default: isDark ? '#0b1020' : '#f7f8fb',
        paper: isDark ? '#0f172a' : '#ffffff',
      },
    },
    // Slightly squarer than before (less “pillowy”)
    shape: { borderRadius: 10 },
    typography: {
      fontFamily:
        'Roboto, ui-sans-serif, system-ui, -apple-system, Segoe UI, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 700 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
          },
          '*::-webkit-scrollbar': {
            width: 10,
            height: 10,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: scrollbarTrack,
            borderRadius: 999,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: scrollbarThumb,
            borderRadius: 999,
            border: `2px solid ${scrollbarTrack}`,
            minHeight: 24,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: scrollbarThumbHover,
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'saturate(180%) blur(10px)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            // Default: keep clean surfaces, but add subtle “height” cues for elevated surfaces
            ...(ownerState?.variant === 'elevation' && (ownerState?.elevation || 0) > 0
              ? {
                  backgroundImage: elevatedSurfaceGradient,
                  // Brighter top inset highlight (acts like a subtle “top border” on stacked cards)
                  boxShadow: `${theme.shadows[ownerState.elevation] || 'none'}, inset 0 1px 0 ${paperTopHighlight}`,
                }
              : {
                  backgroundImage: 'none',
                }),
          }),
        },
      },
      MuiPopover: {
        defaultProps: {
          marginThreshold: 16,
        },
      },
      MuiMenu: {
        defaultProps: {
          PaperProps: {
            sx: {
              maxHeight: 'calc(100vh - 32px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            // A bit squarer than before
            borderRadius: 8,
            boxShadow: 'none',
          },
          contained: {
            boxShadow: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            // Depth: top highlight (like a “light border”) + bottom drop shadow
            border: `1px solid ${cardBorder}`,
            boxShadow: cardShadow,
            backgroundImage: elevatedSurfaceGradient,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            paddingBottom: 8,
          },
          title: {
            fontWeight: 700,
            letterSpacing: -0.2,
          },
          subheader: {
            opacity: 0.8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 700,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderBottom: `1px solid ${alpha(isDark ? '#ffffff' : '#0f172a', 0.12)}`,
          },
          indicator: {
            height: 3,
            borderRadius: 999,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
            paddingTop: 10,
            paddingBottom: 10,
            fontWeight: 700,
            letterSpacing: -0.1,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.04 : 0.03),
          },
          notchedOutline: {
            borderColor: alpha(isDark ? '#ffffff' : '#0f172a', 0.18),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(isDark ? '#ffffff' : '#0f172a', 0.12)}`,
            borderRadius: 10,
            overflow: 'hidden',
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              letterSpacing: -0.1,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.06 : 0.04),
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.02 : 0.015),
            },
          },
          columnHeaders: {
            backgroundColor: alpha(isDark ? '#ffffff' : '#0f172a', isDark ? 0.06 : 0.04),
          },
          toolbarContainer: {
            padding: '8px 10px',
          },
        },
      },
    },
  });

  return responsiveFontSizes(base);
};

// Backwards compatible export used by storybook/tests.
export const darkTheme = createAppTheme('dark');
